<script setup>
import AppBadge from '@/components/atoms/AppBadge'
import AppSkeleton from '@/components/atoms/AppSkeleton'
import HelpHubStepCard from './HelpHubStepCard.vue'
import { useStrapiStore } from '@/stores/StrapiStore'
import { useGetStarted } from '@/hooks/useGetStarted'
import { useHelpHub } from '@/hooks/useHelpHub'
import { GETTING_STARTED_SURFACE } from '@/constants'
import stepImage from '@/assets/images/help-hub/get-started-step.jpg'

const strapiStore = useStrapiStore()
const { steps, stepCount, completedCount, activeKey, canComplete, completeStep, completing } = useGetStarted()
const { helpLink } = useHelpHub()
</script>

<template>
  <!-- The list's shape while the steps load: its title row, the intro and the first cards (the design's blocks, Figma 29796:69280, at this page's full width). -->
  <section v-if="strapiStore.getStartedLoading" role="status" class="flex flex-col gap-y-6 px-4 pt-3 pb-6">
    <span class="sr-only">Loading Get started</span>

    <div class="flex flex-col gap-y-1.5">
      <div class="flex items-center justify-between gap-x-2">
        <AppSkeleton width="w-28" height="h-6" />
        <AppSkeleton width="w-10" height="h-[22px]" rounded="rounded-full" />
      </div>

      <AppSkeleton width="w-5/6" height="h-3.5" />
    </div>

    <!-- A card is its picture and two lines under it. -->
    <AppSkeleton v-for="card in 3" :key="card" height="h-[232px]" rounded="rounded-xl" />
  </section>

  <section v-else class="flex flex-col gap-y-6 px-4 pt-3 pb-6">
    <header class="flex flex-col gap-y-1.5">
      <div class="flex items-center justify-between gap-x-2">
        <h3 class="text-lg font-semibold text-gray-900">Get started</h3>

        <AppBadge size="xs" variant="tint-faint">
          {{ completedCount }}/{{ stepCount }}
        </AppBadge>
      </div>

      <p class="text-sm text-gray-600">Complete these steps to get the most out of your account.</p>
    </header>

    <!-- Each step's key stands in for its help page slug. -->
    <HelpHubStepCard
      v-for="step in steps"
      :key="step.key"
      :to="helpLink(step.key)"
      :number="step.number"
      :title="step.title"
      :description="step.description"
      :step-key="step.key"
      :image="stepImage"
      :active="step.key === activeKey"
      :locked="step.locked"
      :completed="step.completed"
      :completable="canComplete(step)"
      :completing="completing === step.key"
      @complete="completeStep(step, GETTING_STARTED_SURFACE.HUB_LIST)"
    />
  </section>
</template>
